import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { dayKey, catColor } from '@/lib/eventsMock';

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

// Calendario mensile su misura: celle grandi, pallini categoria, stato oggi/selezionato.
export function EventsCalendar({ month, eventsByDay, selectedDate, onSelectDate, onPrev, onNext, categories = [] }) {
  const year = month.getFullYear();
  const m = month.getMonth();
  const first = new Date(year, m, 1);
  const startOffset = (first.getDay() + 6) % 7; // settimana che parte da lunedì
  const daysInMonth = new Date(year, m + 1, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const selKey = selectedDate ? dayKey(selectedDate) : null;

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, m, d));
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-6" data-testid="eventi-calendar">
      <div className="mb-5 flex items-center justify-between">
        <h3 className="font-display text-xl font-semibold text-foreground sm:text-2xl">
          {MONTHS[m]} <span className="text-muted-foreground">{year}</span>
        </h3>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-full transition-transform hover:-translate-y-0.5" onClick={onPrev} data-testid="eventi-calendar-prev-month" aria-label="Mese precedente">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-full transition-transform hover:-translate-y-0.5" onClick={onNext} data-testid="eventi-calendar-next-month" aria-label="Mese successivo">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {WEEKDAYS.map((w) => (
          <div key={w} className="pb-1 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:text-xs">{w}</div>
        ))}
        {cells.map((date, i) => {
          if (!date) return <div key={i} aria-hidden />;
          const key = dayKey(date);
          const evs = eventsByDay[key] || [];
          const isToday = date.getTime() === today.getTime();
          const isSel = selKey === key;
          const cats = Array.from(new Set(evs.map((e) => e.category)));
          return (
            <button
              key={i}
              type="button"
              onClick={() => onSelectDate(date)}
              data-testid={`eventi-calendar-day-${key}`}
              aria-pressed={isSel}
              className={cn(
                'group relative flex aspect-square flex-col items-center justify-between rounded-xl border border-transparent p-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-2',
                isSel ? 'bg-primary text-primary-foreground shadow-sm' : evs.length ? 'bg-accent/30 hover:bg-accent/60' : 'hover:bg-accent/40',
                isToday && !isSel && 'ring-2 ring-inset',
              )}
              style={isToday && !isSel ? { '--tw-ring-color': catColor('osservazioni-astronomiche', 0.45) } : undefined}
            >
              <span className={cn('mt-0.5 tabular-nums', evs.length && !isSel && 'font-semibold text-foreground')}>{date.getDate()}</span>
              {evs.length > 0 ? (
                <span className="mb-0.5 flex items-center justify-center gap-1">
                  {cats.slice(0, 3).map((c, idx) => (
                    <span key={idx} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: isSel ? 'hsl(var(--primary-foreground))' : catColor(c) }} />
                  ))}
                  {cats.length > 3 && <span className={cn('text-[9px] font-semibold', isSel ? 'text-primary-foreground' : 'text-muted-foreground')}>+{cats.length - 3}</span>}
                  <span className="sr-only">{evs.length} eventi</span>
                </span>
              ) : (
                <span className="h-1.5" />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-4">
        {categories.map((c) => (
          <span key={c.slug} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: catColor(c.slug) }} />
            {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}
